import axios from 'axios';
import { createContext, useContext, useCallback, useState, useRef } from 'react';
import db from '../config/firebase';
import { collection, addDoc, onSnapshot } from "firebase/firestore";
import { loadStripe } from '@stripe/stripe-js';
import config from '../config';

// Lazy-load Stripe to avoid top-level await issues
let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(config.REACT_APP_STRIPE_PUBLIC_KEY);
  }
  return stripePromise;
};



const PaymentsContext = createContext()

// Cache TTL (milliseconds) — safety net; primary invalidation is explicit
const PLAN_CACHE_TTL = 5 * 60 * 1000;     // 5 minutes
const CUSTOMER_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const PaymentsContextProvider = ({children}) => {
  const [error, setError] = useState('')

  // Cache refs (useRef to avoid re-rendering entire app tree on cache writes)
  const productsCache = useRef(null);
  const currentPlanCache = useRef({});
  const customerCache = useRef({});

  // In-flight request dedup refs
  const productsInflight = useRef(null);
  const currentPlanInflight = useRef({});
  const customerInflight = useRef({});


  const checkout = async (plan, userId, success_endpoint, cancel_endpoint) => {    
    const docRef = await addDoc(collection(db, "customers", userId, "checkout_sessions"), {
        price: plan.prices.priceId,
        success_url: `${config.REACT_APP_PROD_URL}${success_endpoint}`,
        cancel_url: `${config.REACT_APP_PROD_URL}${cancel_endpoint}`,
    });    
    const unsubscribe = onSnapshot(docRef, async (snap) => {
        const {error, sessionId} = snap.data()
        console.log("SNAP!!", sessionId, error);
        
        if(error) {
            unsubscribe(); // Clean up on error
            alert(`An error occured: ${error.message}`)
            setError(error.message)
        } 
        if(sessionId) {
            unsubscribe(); // Clean up before redirect
            const stripe = await getStripe();
            await stripe.redirectToCheckout({ sessionId });
        }
    })
  }

  const updateQueryLimit = useCallback(async (userId) => {
    try {
      const { data, status } = await axios.post(config.REACT_APP_UPDATE_QUERY_LIMIT_URL, {userId})
      if (status === 200) {
        console.log(data.message, 'Remaining queries: ', data.remaining);
        // Invalidate customer cache — query count has changed
        delete customerCache.current[userId];
        return { success: true, remaining: data.remaining, allowed: data.allowed };
      }
      else {
        setError(data.message)
        return { success: false, remaining: data.remaining, allowed: data.allowed, error: data.message };
      }
    } catch (error) {
      console.log(`Error updating query limit. Error: ${error.message}`);
      setError(error.message)
      return { success: false, error: error.message };
    }
  }, [])


  const checkQueryLimit = useCallback(async (userId) => {
    try {
      const { data, status } = await axios.post(config.REACT_APP_CHECK_QUERY_LIMIT_URL, {userId})
      if (status === 200) {
        return {
          success: true,
          remaining: data.remaining,
          allowed: data.allowed,
          unlimited: data.unlimited,
          noSubscription: data.noSubscription || false,
          freeResetDate: data.freeResetDate || null
        };
      }
      else {
        setError(data.message)
        return { success: false, remaining: 0, allowed: false, unlimited: false, noSubscription: false, error: data.message };
      }
    } catch (error) {
      console.log(`Error checking query limit. Error: ${error.message}`);
      setError(error.message)
      return { success: false, remaining: 0, allowed: false, unlimited: false, noSubscription: false, error: error.message };
    }
  }, [])


  const manageSubscription = async (customerId) => {
    try {
      const { data, status } = await axios.post(config.REACT_APP_BILLING_PORTAL_URL, {customerId, returnUrl: window.location.origin + '/profile?subscription_updated=1'})
      if (status === 200) {
        window.location.assign(data.url);
      }
      else {
        setError(data.message)
      }
    } catch (error) {
      console.log(`Error getting subscription management portal. Error: ${error.message}`);
      setError(error.message)
    }
  }


  const getCustomer = useCallback(async (userId) => {
    // Return cached if valid
    const cached = customerCache.current[userId];
    if (cached && (Date.now() - cached.timestamp < CUSTOMER_CACHE_TTL)) {
      return cached.data;
    }
    // Deduplicate in-flight requests
    if (customerInflight.current[userId]) {
      return customerInflight.current[userId];
    }
    const request = (async () => {
      try {
        const { data, status } = await axios.post(config.REACT_APP_GET_CUSTOMER_URL, {userId})
        if (status === 200) {
          customerCache.current[userId] = { data: data.customer, timestamp: Date.now() };
          return data.customer;
        }
        else {
          setError(data.message);
          return null
        }
      } catch (error) {
        console.log(`Error getting customer. Error: ${error.message}`);
        setError(error.message)
        return null;
      } finally {
        delete customerInflight.current[userId];
      }
    })();
    customerInflight.current[userId] = request;
    return request;
  }, [])


  const getCurrentPlan = useCallback(async (userId) => {
    // Return cached if valid
    const cached = currentPlanCache.current[userId];
    if (cached && (Date.now() - cached.timestamp < PLAN_CACHE_TTL)) {
      return cached.data;
    }
    // Deduplicate in-flight requests
    if (currentPlanInflight.current[userId]) {
      return currentPlanInflight.current[userId];
    }
    const request = (async () => {
      try {
        const { data, status } = await axios.post(config.REACT_APP_GET_SUBSCRIPTION_URL, {userId})
        if (status === 200) {
          currentPlanCache.current[userId] = { data: data.subscription, timestamp: Date.now() };
          return data.subscription
        }
        else {
          setError(data.message);
          console.log(data.message);
          return null
        }
      } catch (error) {
        console.log(`Error getting current plan. Error: ${error.message}`);
        setError(error.message)
        return null;
      } finally {
        delete currentPlanInflight.current[userId];
      }
    })();
    currentPlanInflight.current[userId] = request;
    return request;
  }, [])


  const getProducts = useCallback(async () => {
    // Return cached if available (products never change within a session)
    if (productsCache.current) {
      return productsCache.current;
    }
    // Deduplicate in-flight requests
    if (productsInflight.current) {
      return productsInflight.current;
    }
    const request = (async () => {
      try {
        const { data, status } = await axios.post(config.REACT_APP_GET_PRODUCTS_URL)
        if (status === 200) {
          productsCache.current = data.products;
          return data.products;
        }
        else {
          setError(data.message);
          console.log(data.message);
          return null
        }
      } catch (error) {
        console.log(`Error getting products. Error: ${error.message}`);
        setError(error.message)
        return null
      } finally {
        productsInflight.current = null;
      }
    })();
    productsInflight.current = request;
    return request;
  }, [])


  const invalidatePaymentsCache = useCallback((userId) => {
    if (userId) {
      delete currentPlanCache.current[userId];
      delete customerCache.current[userId];
    } else {
      currentPlanCache.current = {};
      customerCache.current = {};
    }
  }, []);

  const findPlan = (plans, priceId) => {
    return plans.filter(p => p.prices.priceId===priceId)[0]
  }
  

  // const checkout = async (plan, userId, success_endpoint, cancel_endpoint) => {
  //   try {
  //     const res = await axios.post(config.REACT_APP_GET_SESSION_ID_URL, {plan, userId, success_endpoint, cancel_endpoint})
  //     console.log(res);      
      
  //   } catch (error) {
  //     console.log(`Error updating query limit. Error: ${error.message}`);
  //   }
  // }
  

  return (
    <PaymentsContext.Provider value={{checkout, getCurrentPlan, getCustomer, manageSubscription, updateQueryLimit, checkQueryLimit, findPlan, getProducts, invalidatePaymentsCache, error}}>
      {children}
    </PaymentsContext.Provider>
  )
}

export const usePayments = () => {
  return useContext(PaymentsContext)
}